import { useMutation, useQueryClient } from "@tanstack/react-query";
import { socialKeys } from "./queries";
import { socialApi } from "./social-api";

export const useCreatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.createPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      postId,
      data,
    }: {
      postId: string;
      data: { content: string };
    }) => socialApi.updatePost(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, content }: { postId: string; content: string }) =>
      socialApi.createComment(postId, content),
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: socialKeys.comments(postId) });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.deletePost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
};

export const useToggleReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      postId,
      reactionType,
    }: {
      postId: string;
      reactionType?:
        | "like"
        | "stoke"
        | "fire"
        | "heart"
        | "laugh"
        | "skull"
        | "wow"
        | "sad"
        | "angry";
    }) => socialApi.toggleReaction(postId, reactionType),
    onMutate: async ({ postId, reactionType }) => {
      await queryClient.cancelQueries({ queryKey: socialKeys.feed() });
      const previousFeed = queryClient.getQueryData(socialKeys.feed());

      queryClient.setQueryData(socialKeys.feed(), (old: any) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            feed: page.feed.map((post: any) => {
              if (post.id === postId) {
                // Determine optimistic state
                const newReactionTypes = post.reactionTypes
                  ? [...post.reactionTypes]
                  : [];
                if (reactionType && !newReactionTypes.includes(reactionType)) {
                  newReactionTypes.push(reactionType);
                }

                return {
                  ...post,
                  hasReacted: true,
                  reactionCount: post.hasReacted
                    ? post.reactionCount
                    : post.reactionCount + 1,
                  reactionTypes: newReactionTypes,
                };
              }
              return post;
            }),
          })),
        };
      });

      return { previousFeed };
    },
    onError: (err, newReaction, context) => {
      if (context?.previousFeed) {
        queryClient.setQueryData(socialKeys.feed(), context.previousFeed);
      }
    },
    onSettled: () => {
      // Background refetch to ensure absolute synchronization
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
};

export const useSendFriendRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.sendFriendRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
    },
  });
};

export const useAcceptFriendRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.acceptFriendRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({
        queryKey: socialKeys.notifications(),
      });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
      queryClient.invalidateQueries({ queryKey: socialKeys.pendingRequests() });
    },
  });
};

export const useRejectFriendRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.rejectFriendRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      queryClient.invalidateQueries({
        queryKey: socialKeys.notifications(),
      });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
      queryClient.invalidateQueries({ queryKey: socialKeys.pendingRequests() });
    },
  });
};

export const useRemoveFriend = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.removeFriend,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
    },
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: socialKeys.notifications(),
      });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: socialApi.markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: socialKeys.notifications(),
      });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
    },
  });
};
